import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MediaGrid from '@/components/feature/MediaGrid';
import Pagination from '@/components/feature/Pagination';
import FilterPanel from './components/FilterPanel';
import { filterMedia, getPagedList, getTotalPages, type MediaFilters } from '@/lib/media';

const PAGE_SIZE = 12;

const META = {
  movie: {
    title: '电影',
    desc: '汇聚最新热门电影与经典高分佳作，支持按类型、地区、年份快速筛选。',
  },
  tv: {
    title: '电视剧',
    desc: '热播剧集与经典剧集全集回顾，可按类型、地区、年份与播放状态筛选。',
  },
};

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const mediaType: 'movie' | 'tv' = type === 'tv' ? 'tv' : 'movie';
  const meta = META[mediaType];

  const [filters, setFilters] = useState<MediaFilters>({
    genre: 'all',
    region: 'all',
    year: 'all',
    status: 'all',
    sort: 'hot',
  });
  const [page, setPage] = useState(1);

  const list = useMemo(() => filterMedia(mediaType, filters), [mediaType, filters]);
  const totalPages = getTotalPages(list.length, PAGE_SIZE);
  const paged = getPagedList(list, page, PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [filters, mediaType]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const handleFilterChange = (next: MediaFilters) => setFilters(next);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: meta.title }]} />

      <div className="mt-4 mb-5">
        <h1 className="text-2xl font-bold text-foreground-950 font-heading md:text-3xl">
          {meta.title}
          <span className="ml-3 align-middle text-sm font-normal text-foreground-500">
            共 {list.length} 部作品
          </span>
        </h1>
        <p className="mt-2 text-sm text-foreground-500">{meta.desc}</p>
      </div>

      <FilterPanel type={mediaType} filters={filters} onChange={handleFilterChange} />

      <div className="mt-6">
        {paged.length > 0 ? (
          <MediaGrid items={paged} columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" />
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg bg-background-100 py-20 text-center">
            <i className="ri-emotion-unhappy-line text-4xl text-foreground-300" />
            <p className="mt-3 text-sm font-medium text-foreground-700">
              无符合条件的{meta.title}
            </p>
            <p className="mt-1 text-sm text-foreground-500">试试重置筛选条件，或浏览其他分类</p>
            <button
              type="button"
              onClick={() => setFilters({ genre: 'all', region: 'all', year: 'all', status: 'all', sort: 'hot' })}
              className="mt-4 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}